import { useRTC, formatRtcDuration } from "@/context/RTCContext";
import {
  MicrophoneSlash, Microphone, PhoneDisconnect, ArrowsOut,
} from "@phosphor-icons/react";

/**
 * Slim top-of-app banner shown while a call is active. Non-blocking:
 * users continue to work while it hangs at the top of the layout.
 * Uses HelloByFlow tokens exclusively.
 */
export default function ActiveCallBanner() {
  const {
    activeRoom, peers, muted, duration, ringing, roomState,
    toggleMuted, leaveRoom, expandCall, expanded,
  } = useRTC();

  if (!activeRoom || expanded) return null;

  const peerList  = Object.values(peers);
  const primary   = peerList[0]?.user;
  const label = ringing ? "Ringing…"
    : roomState === "joining" ? "Connecting…"
    : roomState === "reconnecting" ? "Reconnecting…"
    : formatRtcDuration(duration);
  const dotColor = ringing ? "#F59E0B"
    : roomState === "reconnecting" ? "#EF4444"
    : "#10B981";

  const displayName = primary?.name
    || (peerList.length > 1 ? `${peerList.length} teammates` : activeRoom.title || "Internal call");

  return (
    <div
      className="w-full flex items-center gap-3 px-4 py-2 border-b border-[var(--hx-line)] bg-white"
      style={{ boxShadow: "0 1px 0 rgba(11,21,38,0.03)" }}
      data-testid="active-call-banner"
    >
      <div className="flex items-center gap-2 text-[13px] font-semibold text-[var(--hx-ink)]">
        <span className="w-2 h-2 rounded-full" style={{ background: dotColor }} />
        <span>Call with</span>
        <span className="text-[var(--hx-blue)]" data-testid="active-call-banner-name">{displayName}</span>
        <span className="text-[var(--hx-ink-3)] font-mono text-[12px]" data-testid="active-call-banner-timer">· {label}</span>
      </div>
      <div className="ml-auto flex items-center gap-1.5">
        <button
          onClick={() => toggleMuted()}
          className={`h-8 px-2.5 rounded-lg text-[12px] font-semibold flex items-center gap-1.5 transition-colors ${
            muted
              ? "bg-[#FEE2E2] text-[#B91C1C] hover:bg-[#FECACA]"
              : "bg-[var(--hx-line-soft)] text-[var(--hx-ink-2)] hover:bg-[var(--hx-line)]"
          }`}
          data-testid="active-call-banner-mute"
        >
          {muted ? <MicrophoneSlash size={13} weight="fill" /> : <Microphone size={13} weight="fill" />}
          {muted ? "Unmute" : "Mute"}
        </button>
        <button
          onClick={expandCall}
          className="h-8 px-2.5 rounded-lg text-[12px] font-semibold flex items-center gap-1.5 bg-[var(--hx-blue-soft)] text-[var(--hx-blue)] hover:bg-[#DBE5FF]"
          data-testid="active-call-banner-expand"
        >
          <ArrowsOut size={13} weight="bold" /> Expand
        </button>
        <button
          onClick={leaveRoom}
          className="h-8 px-2.5 rounded-lg text-[12px] font-semibold text-white bg-[#EF4444] hover:bg-[#DC2626] flex items-center gap-1.5"
          data-testid="active-call-banner-end"
        >
          <PhoneDisconnect size={13} weight="fill" /> End
        </button>
      </div>
    </div>
  );
}
